import type { OnboardingStepKey } from '@crm/shared';
import { Button, cn, Modal } from '@crm/ui';
import { Check } from 'lucide-react';
import type { ReactNode } from 'react';
import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { BILLING_ROUTES, useLicense } from '@/modules/billing';

/**
 * Passo generico da linha do tempo - mesmo formato de `OnboardingStep`, mas
 * com a chave livre. Outros wizards (o de cadastro pelo CTA, por exemplo)
 * reusam o componente com o proprio conjunto de passos.
 */
export interface TimelineStep<TKey extends string = OnboardingStepKey> {
  key: TKey;
  title: string;
  hint: string;
  href: string;
  done: boolean;
  count: number;
  /** Icone ao lado do titulo - opcional, so quem chama com icone o mostra. */
  icon?: ReactNode;
}

/**
 * Os passos em linha do tempo. O que ja foi some do caminho critico (fica
 * marcado e apagado), e o proximo pendente e o unico com botao em destaque -
 * guia com oito botoes iguais nao guia ninguem.
 */
export function OnboardingTimeline<TKey extends string = OnboardingStepKey>({
  steps,
  compact,
  customStep,
  readOnly,
}: {
  steps: TimelineStep<TKey>[];
  compact?: boolean;
  /** Troca o hint+botao do passo atual por um mini-wizard proprio. */
  customStep?: { key: TKey; render: () => ReactNode };
  /**
   * So a lista, sem hint nem botao "Configurar" - usado quando o conteudo do
   * passo atual mora em outro lugar da tela, nao aqui dentro.
   */
  readOnly?: boolean;
}) {
  const proximo = steps.find((step) => !step.done);

  const license = useLicense();
  const navigate = useNavigate();
  const [upgradeStep, setUpgradeStep] = useState<TimelineStep<TKey> | null>(null);

  // WhatsApp (bot) e Campanhas (marketing por WhatsApp) pedem um dos dois
  // recursos no plano - sem nenhum, "Configurar" pede upgrade em vez de abrir
  // a tela de configuracao.
  const plano = license.data?.plan;
  const semRecursoDeWhatsapp = !plano?.whatsappBot && !plano?.whatsappMarketing;

  function precisaDeUpgrade(step: TimelineStep<TKey>): boolean {
    return semRecursoDeWhatsapp && (step.key === 'WHATSAPP' || step.key === 'CAMPAIGNS');
  }

  return (
    <>
      <ol className="relative space-y-1">
        {steps.map((step, indice) => {
          const atual = step.key === proximo?.key;
          const conteudoProprio = atual && step.key === customStep?.key;

          return (
            <li key={step.key} className="relative flex gap-3 pb-1">
              {/* A linha liga um passo ao outro; o ultimo nao puxa linha. */}
              {indice < steps.length - 1 ? (
                <span
                  aria-hidden
                  className="absolute left-3.5 top-8 h-[calc(100%-1rem)] w-px bg-border"
                />
              ) : null}

              <span
                className={cn(
                  'relative z-10 mt-1 flex h-7 w-7 shrink-0 items-center justify-center rounded-full border text-xs font-medium',
                  step.done
                    ? 'border-primary bg-primary text-primary-foreground'
                    : atual
                      ? 'border-primary bg-background text-primary'
                      : 'border-border bg-background text-muted-foreground',
                )}
              >
                {step.done ? <Check className="h-4 w-4" /> : indice + 1}
              </span>

              <div className={cn('min-w-0 flex-1 pb-2', step.done && !compact ? 'opacity-60' : '')}>
                <div className="flex flex-wrap items-center gap-x-2">
                  {step.icon ? (
                    <span className="text-muted-foreground [&_svg]:h-4 [&_svg]:w-4">
                      {step.icon}
                    </span>
                  ) : null}
                  <span className="font-medium">{step.title}</span>
                  {step.count > 0 ? (
                    <span className="text-xs text-muted-foreground">{step.count}</span>
                  ) : null}
                </div>

                {conteudoProprio ? (
                  <div className="mt-2">{customStep?.render()}</div>
                ) : readOnly ? (
                  atual ? (
                    <p className="text-sm text-muted-foreground">{step.hint}</p>
                  ) : null
                ) : (
                  <>
                    {!step.done || !compact ? (
                      <p className="text-sm text-muted-foreground">{step.hint}</p>
                    ) : null}

                    {!step.done ? (
                      precisaDeUpgrade(step) ? (
                        <Button
                          size="sm"
                          variant={atual ? 'default' : 'outline'}
                          className="mt-2"
                          onClick={() => setUpgradeStep(step)}
                        >
                          Configurar
                        </Button>
                      ) : (
                        <Button
                          size="sm"
                          variant={atual ? 'default' : 'outline'}
                          className="mt-2"
                          asChild
                        >
                          <Link to={step.href}>Configurar</Link>
                        </Button>
                      )
                    ) : null}
                  </>
                )}
              </div>
            </li>
          );
        })}
      </ol>

      <Modal
        open={upgradeStep !== null}
        onClose={() => setUpgradeStep(null)}
        title="Recurso fora do seu plano"
        size="small"
        actions={[
          { label: 'Cancelar', variant: 'ghost', onClick: () => setUpgradeStep(null) },
          {
            label: 'Ver planos de upgrade',
            onClick: () => {
              setUpgradeStep(null);
              navigate(BILLING_ROUTES.PLANS);
            },
          },
        ]}
      >
        <p className="text-sm text-muted-foreground">
          "{upgradeStep?.title}" precisa de um plano com WhatsApp Bot ou WhatsApp Marketing. Faca
          upgrade do seu plano para liberar esse recurso.
        </p>
      </Modal>
    </>
  );
}
